import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Coins, TrendingUp, PiggyBank, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const WeeklySummaryPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await api.get('/coin-world/weekly-summary');
      setSummary(response.data);
    } catch (error) {
      toast.error('Could not load summary');
    } finally {
      setLoading(false);
    }
  };

  const getProgressEmoji = (value, thresholds) => {
    if (value >= thresholds[2]) return '🌟';
    if (value >= thresholds[1]) return '⭐';
    if (value >= thresholds[0]) return '✨';
    return '🌱';
  };

  const getMotivationalMessage = () => {
    const totalActivity = (summary?.coins_earned || 0) + (summary?.coins_saved || 0) + (summary?.coins_grown || 0);
    
    if (totalActivity >= 100) return "You're a Coin World Champion! 🏆";
    if (totalActivity >= 50) return "Amazing progress this week! 🌟";
    if (totalActivity >= 20) return "You're doing great! Keep it up! 💪";
    return "Every coin counts! Keep going! 🌱";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">📊</div>
          <p className="text-xl text-slate-600">Loading your summary...</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      id: 'earned',
      label: 'Coins Earned',
      value: summary?.coins_earned || 0,
      icon: '💰',
      color: 'bg-yellow-100',
      emoji: getProgressEmoji(summary?.coins_earned || 0, [10, 30, 50])
    },
    {
      id: 'saved',
      label: 'Coins Saved',
      value: summary?.coins_saved || 0,
      icon: '🏦',
      color: 'bg-blue-100',
      emoji: getProgressEmoji(summary?.coins_saved || 0, [5, 15, 30])
    },
    {
      id: 'grown',
      label: 'Coins Grown',
      value: summary?.coins_grown || 0,
      icon: '🌱',
      color: 'bg-green-100',
      emoji: getProgressEmoji(summary?.coins_grown || 0, [2, 5, 10])
    },
  ];

  const maxValue = Math.max(...stats.map(s => s.value), 1);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="summary-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center">
          <button
            onClick={() => navigate('/coin-world')}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 font-fredoka ml-3">Coin Bank Day</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Banner */}
        <div className="card-bubbly bg-gradient-to-br from-orange-50 to-orange-100 text-center">
          <div className="text-6xl mb-3">📊</div>
          <h2 className="text-2xl font-black text-slate-900 font-fredoka mb-2">
            Your Weekly Report
          </h2>
          <p className="text-slate-600">{getMotivationalMessage()}</p>
        </div>

        {/* Total Balance */}
        <div className="card-bubbly bg-gradient-to-br from-primary/20 to-primary/10 text-center">
          <p className="text-sm text-slate-600 mb-1">Total Coins</p>
          <p className="text-5xl font-black text-slate-900 flex items-center justify-center gap-2">
            <Coins className="w-10 h-10 text-primary" />
            {summary?.total_coins || 0}
          </p>
          <p className="text-sm text-slate-500 mt-2">
            + {summary?.vault_total || 0} coins in your vault
          </p>
        </div>

        {/* Stats Bars */}
        <div className="card-bubbly">
          <h3 className="font-bold text-slate-900 mb-4">This Week's Activity</h3>
          
          <div className="space-y-4">
            {stats.map((stat) => {
              const barWidth = maxValue > 0 ? (stat.value / maxValue) * 100 : 0;
              
              return (
                <div key={stat.id} data-testid={`stat-${stat.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{stat.icon}</span>
                      <span className="font-medium text-slate-700">{stat.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900">{stat.value}</span>
                      <span className="text-xl">{stat.emoji}</span>
                    </div>
                  </div>
                  
                  <div className="h-8 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${stat.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(barWidth, 5)}%` }}
                    >
                      {barWidth > 20 && (
                        <span className="text-xs font-bold text-slate-700">{stat.value}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Streak */}
        <div className={`card-bubbly ${summary?.save_streak > 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
          <div className="flex items-center gap-4">
            <div className="text-5xl">
              {summary?.save_streak >= 7 ? '🔥' : summary?.save_streak >= 3 ? '⚡' : '✨'}
            </div>
            <div>
              <p className="font-bold text-slate-900 text-lg">
                {summary?.save_streak || 0} Day Save Streak
              </p>
              <p className="text-slate-600 text-sm">
                {summary?.save_streak >= 3 
                  ? 'Your Save Jar is earning bonus growth!' 
                  : `Save ${3 - ((summary?.save_streak || 0) % 3)} more days for bonus growth!`}
              </p>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="card-bubbly">
          <h3 className="font-bold text-slate-900 mb-3">Weekly Achievements</h3>
          
          <div className="space-y-2">
            <div className={`flex items-center gap-3 p-3 rounded-xl ${(summary?.coins_earned || 0) > 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
              <span className="text-2xl">{(summary?.coins_earned || 0) > 0 ? '✅' : '⬜'}</span>
              <span className={`${(summary?.coins_earned || 0) > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                Earned coins this week
              </span>
            </div>
            
            <div className={`flex items-center gap-3 p-3 rounded-xl ${(summary?.coins_saved || 0) > 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
              <span className="text-2xl">{(summary?.coins_saved || 0) > 0 ? '✅' : '⬜'}</span>
              <span className={`${(summary?.coins_saved || 0) > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                Saved coins in your vault
              </span>
            </div>
            
            <div className={`flex items-center gap-3 p-3 rounded-xl ${(summary?.coins_grown || 0) > 0 ? 'bg-green-50' : 'bg-slate-50'}`}>
              <span className="text-2xl">{(summary?.coins_grown || 0) > 0 ? '✅' : '⬜'}</span>
              <span className={`${(summary?.coins_grown || 0) > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                Grew coins through saving
              </span>
            </div>
            
            <div className={`flex items-center gap-3 p-3 rounded-xl ${(summary?.save_streak || 0) >= 7 ? 'bg-green-50' : 'bg-slate-50'}`}>
              <span className="text-2xl">{(summary?.save_streak || 0) >= 7 ? '✅' : '⬜'}</span>
              <span className={`${(summary?.save_streak || 0) >= 7 ? 'text-green-700' : 'text-slate-400'}`}>
                7-day save streak
              </span>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="card-bubbly bg-purple-50">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            Tips for Next Week
          </h3>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-purple-500 mt-0.5" />
              <span>Save a little bit every day to build your streak!</span>
            </li>
            <li className="flex items-start gap-2">
              <PiggyBank className="w-4 h-4 text-purple-500 mt-0.5" />
              <span>Plant seeds early so they grow by next week!</span>
            </li>
            <li className="flex items-start gap-2">
              <Coins className="w-4 h-4 text-purple-500 mt-0.5" />
              <span>Complete your daily Brain Gym Jr. for more coins!</span>
            </li>
          </ul>
        </div>

        {/* Encouragement */}
        <div className="text-center py-4">
          <p className="text-lg text-slate-600 font-medium">
            Keep learning and growing! You're doing amazing! 🌟
          </p>
        </div>
      </main>
    </div>
  );
};

export default WeeklySummaryPage;
